import { SpinnerSize } from '@amzn/stencil-react-components/spinner';
import { Auth, CognitoUser } from '@aws-amplify/auth';
import Amplify from '@aws-amplify/core';
import React, { createContext, useEffect, useState } from 'react';
import { Cookies } from 'react-cookie';
import { browserHistory } from 'src/AppRoutes';
import {
  amplifyConfig,
  cognitoUrl
} from 'src/components/Authentication/Amplify';
import ContentLoader from 'src/components/ContentLoader';
import Session from 'src/components/Session';
import {
  LOGIN_VISIT_REDIRECT_PATH,
  USER_AUTHORIZATION_TOKEN_KEY,
  USER_SESSION_CACHED,
  USER_SESSION_REFRESHED,
  USER_SESSION_STATE_KEY
} from 'src/constants/AccessConstants';
import { logger } from 'src/logger/KatalLoggerClient';
import { AuthenticatedUser } from 'src/models/AuthenticatedUser';
import { CookieSetOptions } from 'universal-cookie/cjs/types';

Amplify.configure(amplifyConfig);
const COOKIE_TTL = 300000000;

interface AuthenticatedRouteProps {
  children: React.ReactNode;
}

interface AuthenticatedCognitoUser extends CognitoUser {
  signInUserSession: {
    idToken: {
      jwtToken: string;
      payload: {
        preferred_username: string;
        given_name: string;
        family_name: string;
        'custom:EMPLOYEE_ID': string;
      };
    };
  };
}
interface UserContextProps {
  authenticatedUser: AuthenticatedUser;
  setAuthenticatedUser: React.Dispatch<React.SetStateAction<AuthenticatedUser>>;
}

export const UserContext = createContext<UserContextProps>(
  {} as UserContextProps
);

/**
 * Authenticated Route component to render children after authenticating user via cognito-midway
 */
const Authentication = ({ children }: AuthenticatedRouteProps) => {
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser>(
    {} as AuthenticatedUser
  );

  const cookies = new Cookies();
  const cookieStorageOptions: CookieSetOptions = {
    path: '/',
    secure: true,
    sameSite: 'strict',
    domain: window.location.hostname,
    expires: new Date(Date.now() + COOKIE_TTL)
  };

  const onAuthenticationSuccess = (
    authenticatedCognitoUser: AuthenticatedCognitoUser
  ) => {
    cookies.set(
      USER_AUTHORIZATION_TOKEN_KEY,
      authenticatedCognitoUser.signInUserSession.idToken.jwtToken,
      cookieStorageOptions
    );
    // Read identity from the ID token (populated by the Federate SAML mapping) so the SPA
    // does not depend on the aws.cognito.signin.user.admin scope / GetUser call.
    const idTokenPayload =
      authenticatedCognitoUser.signInUserSession.idToken.payload;
    const userDetails: AuthenticatedUser = {
      alias: idTokenPayload.preferred_username,
      firstName: idTokenPayload.given_name,
      lastName: idTokenPayload.family_name,
      employeeId: idTokenPayload['custom:EMPLOYEE_ID']
    };
    setAuthenticatedUser(userDetails);
    setIsUserAuthenticated(true);
  };

  const redirectForAuthentication = () => {
    cookies.set(
      USER_SESSION_STATE_KEY,
      USER_SESSION_REFRESHED,
      cookieStorageOptions
    );
    cookies.set(
      LOGIN_VISIT_REDIRECT_PATH,
      `${location.pathname}${location.search}`,
      cookieStorageOptions
    );
    window.location.replace(cognitoUrl);
  };

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((authenticatedCognitoUser: AuthenticatedCognitoUser) => {
        if (cookies.get(USER_SESSION_STATE_KEY) === USER_SESSION_REFRESHED) {
          cookies.set(
            USER_SESSION_STATE_KEY,
            USER_SESSION_CACHED,
            cookieStorageOptions
          );
          browserHistory.replace(cookies.get(LOGIN_VISIT_REDIRECT_PATH));
          onAuthenticationSuccess(authenticatedCognitoUser);
          console.log('User Authenticated', authenticatedCognitoUser);
        } else if (
          cookies.get(USER_SESSION_STATE_KEY) === USER_SESSION_CACHED
        ) {
          onAuthenticationSuccess(authenticatedCognitoUser);
        } else {
          console.log('Authentication failed', authenticatedCognitoUser);
          redirectForAuthentication();
        }
      })
      .catch((error: any) => {
        void logger
          .error('Error occurred during user authentication', error)
          .then();
        redirectForAuthentication();
      });
  }, []);

  if (isUserAuthenticated) {
    if (Session.getCurrentSessionId() === undefined) {
      Session.createSessionIdAndStoreInCookies();
    } else {
      Session.setSessionRefresh();
    }
    return (
      <UserContext.Provider value={{ authenticatedUser, setAuthenticatedUser }}>
        <React.Fragment>{children}</React.Fragment>
      </UserContext.Provider>
    );
  } else {
    return (
      <ContentLoader
        size={SpinnerSize.Large}
        loadingText={'Authenticating'}
        fontSize={'T400'}
        height={'95vh'}
      />
    );
  }
};

export default Authentication;
